/**
 * Table fit — one sizing rule for table pages. The web renderer (SlideRenderer.tsx), the PDF / pixel-perfect PPTX
 * (pdfRenderer.ts) and the editable PPTX (documentGenerator.ts) all take row height and type size from here, so a
 * table is the same size everywhere. The app (src/cowork/omniad/components/slides/tableFit.ts) and the backend
 * (src/engine/tableFit.ts) keep identical copies with identical tests: change both together.
 *
 * Units: heights in percent of the page height (cqh), type in percent of the page width (cqw); the page is 16:9.
 * Rows shrink with the row count down to a legible floor; a table that does not fit even there splits into
 * consecutive pages (splitTableSlide): header repeated, "(cont.)" / "(forts.)" on the title, totals on the last page.
 */

export const TABLE_FIT = {
  /** Cell type at full size (cqw): the deck's body step. */
  BODY: 1.4,
  /** Header type at full size (cqw): the caption step. Never larger than the cell type. */
  HEAD: 0.95,
  /** Legibility floor: type never goes below 60 % of the body size. */
  MIN_SCALE: 0.6,
  /** Line height of a cell. */
  LINE: 1.2,
  /** Vertical cell padding (cqh, each side) at full size and at the floor. */
  PAD_MAX: 1.4,
  PAD_MIN: 0.2,
  /** The content area: 36 % from the top to 11 % from the bottom, clear of the footer, page number and progress bar. */
  TOP: 36,
  BOTTOM: 11,
  /** Content width (cqw): 7.5 % gutters. */
  WIDTH: 85,
} as const;

/** Height of the content area (cqh). */
export const TABLE_AREA_H = 100 - TABLE_FIT.TOP - TABLE_FIT.BOTTOM;
/** 1 cqw in cqh on a 16:9 page. */
const ASPECT = 16 / 9;
const r3 = (n: number) => Math.round(n * 1000) / 1000;

/** Start scale for a column count: more than 4 columns start at 82 % (the old dense type), more than 6 at 70 %. */
export function tableMaxScale(colCount: number): number { return colCount > 6 ? 0.7 : colCount > 4 ? 0.82 : 1; }
/** Vertical cell padding (cqh, each side) at a type scale: PAD_MAX at full size down to PAD_MIN at the floor. */
export function tablePad(scale: number): number {
  const { MIN_SCALE, PAD_MAX, PAD_MIN } = TABLE_FIT;
  const k = Math.max(0, Math.min(1, (scale - MIN_SCALE) / (1 - MIN_SCALE)));
  return r3(PAD_MIN + (PAD_MAX - PAD_MIN) * k);
}
/** One text line (cqh) at a type scale. */
export function tableLineH(scale: number): number { return r3(TABLE_FIT.BODY * scale * ASPECT * TABLE_FIT.LINE); }
/** A one-line row (cqh) at a type scale. */
export function tableRowH(scale: number): number { return r3(tableLineH(scale) + 2 * tablePad(scale)); }

export interface TableFit {
  /** Row height (cqh); every row, the header row included, is this tall (more when a cell wraps). */
  rowH: number;
  /** Type scale: cells are BODY × fontScale cqw, headers min(HEAD, BODY × fontScale). */
  fontScale: number;
  /** Vertical cell padding (cqh, each side). */
  pad: number;
  /** False when the rows do not fit even at the floor: the page is to be split. */
  fits: boolean;
}

/**
 * The largest type scale (1 % steps, from the column-count start down to the floor) at which `rowCount` rows —
 * header row(s), body rows and the total row — plus `extraLines` wrapped lines fit `availableHeight` (cqh).
 */
export function tableFit(rowCount: number, colCount: number, availableHeight: number = TABLE_AREA_H, extraLines = 0): TableFit {
  const top = Math.round(tableMaxScale(colCount) * 100); const floor = Math.round(TABLE_FIT.MIN_SCALE * 100);
  for (let p = top; p >= floor; p--) {
    const s = p / 100;
    if (rowCount * tableRowH(s) + extraLines * tableLineH(s) <= availableHeight + 1e-6) return { rowH: tableRowH(s), fontScale: s, pad: tablePad(s), fits: true };
  }
  const s = TABLE_FIT.MIN_SCALE;
  return { rowH: tableRowH(s), fontScale: s, pad: tablePad(s), fits: false };
}

// ─── Cells ──────────────────────────────────────────────────────────────────

/** A cell's text: plain strings, or `{ text, avatarUrl }` creator cells. */
export function cellText(c: unknown): string { return c && typeof c === 'object' ? String((c as any).text ?? '') : String(c ?? ''); }
/** Cell text without **bold** / *italic* markers. */
function plainText(c: unknown): string { return cellText(c).replace(/\*\*/g, '').replace(/(^|\s)\*([^*]+)\*(?=\s|$)/g, '$1$2').trim(); }

const TOTAL_RE = /^(total|totalt|summa|sum)\b/i;
/** Index of the totals row: the last row when its first cell reads Total / Totalt / Summa / Sum; otherwise -1. */
export function totalRowIndex(rows: unknown[][]): number {
  const last = rows.length - 1;
  return last >= 0 && Array.isArray(rows[last]) && TOTAL_RE.test(plainText(rows[last][0])) ? last : -1;
}

const NUMBER_RE = /^(?:[€$£]\s?|(?:sek|nok|dkk|eur|usd|gbp|kr)\.?\s?)?[-−–+]?\(?[-−]?\d[\d\s\u00a0\u202f.,']*\)?\s?(?:%|‰|×|x|pp|k|m|mn|bn|mkr|tkr|kr|:-|sek|nok|dkk|eur|usd|gbp|€|\$|£)?\.?$/i;
/** "220 000", "SEK 1.2M", "12,5 %", "(3.9)", "−4 %", "3.2×" — a figure, not a word or a date. */
export function isNumberLike(c: unknown): boolean { const t = plainText(c); return t.length > 0 && t.length <= 32 && NUMBER_RE.test(t); }
/** Number-like text with its spaces made non-breaking, so "220 000 kr" never wraps mid-number. */
export function keepNumberTogether(text: string): string { return isNumberLike(text) ? text.replace(/ /g, '\u00a0') : text; }
const isBlank = (c: unknown) => /^(|—|–|-|n\/a)$/i.test(plainText(c));
/** Columns (after the first, which holds the labels) whose filled cells are at least 80 % figures: right-aligned, never wrapped. */
export function numberColumns(rows: unknown[][], colCount: number): boolean[] {
  return Array.from({ length: colCount }, (_, j) => {
    if (j === 0) return false;
    const filled = rows.map((r) => (Array.isArray(r) ? r[j] : undefined)).filter((c) => !isBlank(c));
    return filled.length > 0 && filled.filter(isNumberLike).length >= 0.8 * filled.length;
  });
}

/** Average glyph width in em: body text, and letter-spaced capitals in the header row. */
const CHAR_EM = 0.52;
const HEAD_CHAR_EM = 0.8;
/** Lines a cell's text takes at `perLine` characters a line (explicit line breaks kept). */
function linesOf(text: string, perLine: number): number { return text.split('\n').reduce((n, l) => n + Math.max(1, Math.ceil(l.length / perLine)), 0); }
/** Extra lines (beyond one a row) the wrapping text cells add at a type scale, estimated with equal-width columns. */
export function tableExtraLines(headers: unknown[], rows: unknown[][], colCount: number, scale: number, numeric: boolean[] = []): number {
  const colW = TABLE_FIT.WIDTH / Math.max(1, colCount) - 1; // less the 1cqw gap between cells
  const per = (em: number, size: number) => Math.max(4, Math.floor(colW / (size * em)));
  const bodyPer = per(CHAR_EM, TABLE_FIT.BODY * scale); const headPer = per(HEAD_CHAR_EM, Math.min(TABLE_FIT.HEAD, TABLE_FIT.BODY * scale));
  const rowLines = (row: unknown[], perLine: number) => (Array.isArray(row) ? row : []).reduce<number>((m, c, j) => (numeric[j] ? m : Math.max(m, linesOf(plainText(c), perLine))), 1);
  return (headers.length ? rowLines(headers, headPer) - 1 : 0) + rows.reduce<number>((n, r) => n + rowLines(r, bodyPer) - 1, 0);
}

// ─── Layout ─────────────────────────────────────────────────────────────────

export interface TableLayout extends TableFit {
  cols: number;
  /** Header rows drawn: the column headers, plus the group row when there is one. */
  headerRows: number;
  /** Every row drawn: header rows, body rows and `table.total`. */
  rowCount: number;
  /** Per column: right-aligned figures. */
  numeric: boolean[];
  /** The totals row (last row reading Total / Totalt / Summa / Sum), or -1. */
  totalIndex: number;
  /** Body rows one page holds at the floor (one-line rows). */
  maxRows: number;
  /** Pages the table needs: 1 when it fits. */
  pages: number;
}

function parts(table: any) {
  const headers: unknown[] = Array.isArray(table?.headers) ? table.headers : [];
  const rows: unknown[][] = Array.isArray(table?.rows) ? table.rows.map((r: unknown) => (Array.isArray(r) ? r : [])) : [];
  const total: unknown[] | null = Array.isArray(table?.total) ? table.total : null;
  const cols = Math.max(headers.length, ...rows.map((r) => r.length), total?.length ?? 0, 1);
  const headerRows = 1 + (Array.isArray(table?.groups) && table.groups.length ? 1 : 0);
  return { headers, rows, total, cols, headerRows };
}

/** Fit for a set of body rows: iterate so wrapped lines are estimated at the scale they will render at. */
function fitRows(headers: unknown[], rows: unknown[][], withTotal: boolean, cols: number, headerRows: number, numeric: boolean[], availableHeight: number): TableFit {
  const rowCount = headerRows + rows.length + (withTotal ? 1 : 0);
  let fit = tableFit(rowCount, cols, availableHeight);
  for (let i = 0; i < 4; i++) {
    const next = tableFit(rowCount, cols, availableHeight, tableExtraLines(headers, rows, cols, fit.fontScale, numeric));
    if (next.fontScale === fit.fontScale && next.fits === fit.fits) return next;
    fit = next;
  }
  return fit;
}

/** Body-row ranges [from, to) per page: as few pages as fit at the floor, rows shared evenly, `table.total` on the last. */
export function tablePageRanges(table: any, availableHeight: number = TABLE_AREA_H): Array<[number, number]> {
  const { headers, rows, total, cols, headerRows } = parts(table);
  const numeric = numberColumns(rows, cols);
  const n = rows.length;
  if (n <= 1) return [[0, n]];
  for (let p = 1; p <= n; p++) {
    const base = Math.floor(n / p); const extra = n % p;
    const ranges: Array<[number, number]> = [];
    for (let i = 0, a = 0; i < p; i++) { const b = a + base + (i < extra ? 1 : 0); ranges.push([a, b]); a = b; }
    if (ranges.every(([a, b], i) => fitRows(headers, rows.slice(a, b), !!total && i === p - 1, cols, headerRows, numeric, availableHeight).fits)) return ranges;
  }
  return rows.map((_, i) => [i, i + 1] as [number, number]);
}

/** Size, alignment and page count for a table page's `table`. */
export function tableLayout(table: any, availableHeight: number = TABLE_AREA_H): TableLayout {
  const { headers, rows, total, cols, headerRows } = parts(table);
  const numeric = numberColumns(rows, cols);
  const fit = fitRows(headers, rows, !!total, cols, headerRows, numeric, availableHeight);
  const maxRows = Math.max(1, Math.floor((availableHeight + 1e-6) / tableRowH(TABLE_FIT.MIN_SCALE)) - headerRows);
  return { ...fit, cols, headerRows, rowCount: headerRows + rows.length + (total ? 1 : 0), numeric, totalIndex: totalRowIndex(rows), maxRows, pages: fit.fits ? 1 : tablePageRanges(table, availableHeight).length };
}

// ─── Continuation pages ─────────────────────────────────────────────────────

const NORDIC_RE = /^(sv|swe|swedish|svenska|no|nb|nn|nor|norwegian|norsk|da|dan|danish|dansk)\b/i;
/** Deck language for the continuation label: the stated language when known, else read off the deck's text. */
export function tableLanguage(hint?: unknown, slides?: any[]): 'sv' | 'en' {
  if (typeof hint === 'string' && hint.trim()) return NORDIC_RE.test(hint.trim()) ? 'sv' : 'en';
  const text = (slides || []).map((s) => [s?.title, s?.eyebrow, s?.kicker, ...(Array.isArray(s?.table?.headers) ? s.table.headers : []), ...(Array.isArray(s?.table?.rows) ? s.table.rows.map((r: unknown[]) => (Array.isArray(r) ? r[0] : '')) : [])].map(cellText).join(' ')).join(' ').toLowerCase();
  const count = (re: RegExp) => (text.match(re) || []).length;
  const sv = count(/\b(och|för|att|med|som|av|på|är|inte|till|från|totalt|summa|kampanj\w*|plattform\w*|månad\w*|kostnad\w*|vecka|veckor|vår|våra|er|ert)\b/g);
  const en = count(/\b(the|and|for|with|of|to|is|not|from|total|campaign\w*|platform\w*|month\w*|cost\w*|week\w*|our|your)\b/g);
  return sv >= 2 && sv > en ? 'sv' : 'en';
}
/** "(cont.)", or "(forts.)" in a Swedish (Nordic) deck. */
export function continuedLabel(language?: unknown, slides?: any[]): string { return tableLanguage(language, slides) === 'sv' ? '(forts.)' : '(cont.)'; }
const CONT_RE = /\s*\((?:cont\.|forts\.)\)\s*$/i;

/** A regular table page (not the matrix / glossary compositions). */
export function isSplittableTable(slide: any): boolean {
  return slide?.type === 'table' && slide.variant !== 'matrix' && slide.variant !== 'glossary' && Array.isArray(slide.table?.rows);
}

/**
 * A table page as the pages it needs: itself when it fits, else consecutive copies with a share of the rows each —
 * header row (and groups) repeated, the title marked "(cont.)" / "(forts.)" from the second page, the kicker and
 * notes on the first page only, the footnote, `table.total` and the totals row on the last. Idempotent: every page
 * returned fits, so splitting it again returns it unchanged.
 */
export function splitTableSlide(slide: any, language?: unknown): any[] {
  if (!isSplittableTable(slide)) return [slide];
  const ranges = tablePageRanges(slide.table);
  if (ranges.length <= 1) return [slide];
  const label = continuedLabel(language, [slide]);
  const emphasis: number[] = Array.isArray(slide.table.emphasis) ? slide.table.emphasis.map(Number) : [];
  const title = typeof slide.title === 'string' ? slide.title.replace(CONT_RE, '') : slide.title;
  return ranges.map(([a, b], i) => {
    const last = i === ranges.length - 1;
    const page = JSON.parse(JSON.stringify(slide));
    page.table.rows = page.table.rows.slice(a, b);
    if (emphasis.length) page.table.emphasis = emphasis.filter((e) => e >= a && e < b).map((e) => e - a);
    if (!last) { delete page.table.total; delete page.footnote; }
    if (i > 0) {
      if (typeof title === 'string' && title.trim()) page.title = `${title} ${label}`;
      delete page.kicker; delete page.notes; delete page.source; // a table linked to its data (media plans) stays linked on its first page only
      if (typeof page.id === 'string' && page.id) page.id = `${page.id}-${i + 1}`;
    }
    return page;
  });
}

/** Every table page split into the pages it needs (exports call this so nothing is ever cut off). */
export function expandTableSlides(slides: any[], language?: unknown): any[] {
  if (!slides.some((s) => isSplittableTable(s))) return slides;
  const lang = tableLanguage(language, slides);
  return slides.flatMap((s) => splitTableSlide(s, lang));
}
